import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'

import { httpService } from '@/api/v1/http.service'
import DEFAULT_PAGE_SIZE from '@/constants/default-page-size'
import type { PaginationParams } from '@/types/params'
import type { Order } from '@/types/sorting'
import { getOrdering } from '@/utils/get-ordering'

import {
  parseSemester,
  schoolYearSchema
} from '../constants/school-year-schemas'
import type { SchoolYear, SchoolYearAndBreakToggle } from '../types'

const responseSchema = z.array(schoolYearSchema)

type APIResponse = z.infer<typeof responseSchema>

const parseResponse = (response: APIResponse): SchoolYear[] =>
  response.map(item => ({
    id: item.id,
    name: item.name,
    status: item.status,
    startDate: item.start_date,
    endDate: item.end_date,
    firstSemester: parseSemester(item.semesters[0]),
    secondSemester: parseSemester(item.semesters[1])
  }))

type Filters = {
  search?: string
  status?: SchoolYearAndBreakToggle
  sort?: string
  order?: Order
}

export type SchoolYearParams = Filters & PaginationParams

export const useSchoolYears = (params: SchoolYearParams = {}) =>
  useQuery({
    queryKey: ['panelPeriodsYearsList', params],
    staleTime: 60 * 1000,
    queryFn: async () => {
      const response = await httpService.panel.panelPeriodsYearsList({
        page: params.page ?? 1,
        // @ts-expect-error Waits for backend fix
        page_size: params.pageSize ?? DEFAULT_PAGE_SIZE,
        status: params.status === 'past' ? ['archived'] : ['active', 'future'],
        search: params.search,
        ordering: params.order
          ? getOrdering(params)
          : params.status === 'past'
            ? '-start_date'
            : 'start_date',
        fetchKeys: {
          id: true,
          name: true,
          semesters: true,
          start_date: true,
          end_date: true,
          status: true
        }
      })

      const parsed = parseResponse(responseSchema.parse(response.results))
      return { list: parsed, count: response.count }
    }
  })
